import { datadogLogs } from '@datadog/browser-logs'
import { fromInteger, toInteger } from '@repo/money'
import { Effect } from 'effect'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { v7 as uuidv7 } from 'uuid'
import { BET_CONFIG, FALLBACK_DEFAULT_BET_MULTIPLIER } from 'src/config'
import { useGetAutoBooster, useGetActiveBonus, useInventoryStore } from 'src/features/inventory'
import { useAutoApplyClassicFreeBet } from 'src/features/inventory/hooks'
import { isItemApplicableToGame } from 'src/features/inventory/utils/isItemApplicableToGame'
import { useGetCurrentBet } from 'src/hooks/useGetCurrentBet'
import { useBetLock } from 'src/hooks/useBetLock'
import { logClickRefusedAtLock } from 'src/utils/hardCloseClickRefused'
import { usePayoutCalculation } from 'src/hooks/usePayoutCalculation'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { useClientSideTranslation } from 'src/services/i18n'
import { emitBalanceInsufficient } from 'src/services/postMessage/emitters'
import { useBetActions, useIsUnacceptedBet } from 'src/store/bet'
import { useGetCurrentLanguage, useGetGameBetConfig } from 'src/store/game'
import {
  useGetIsPlacingNextRaceBet,
  useGetNextRaceBet,
  useNextRaceBetActions
} from 'src/store/nextRaceBet'
import { usePlayerActions } from 'src/store/player'
import { useGetPlayerChannel } from 'src/store/socket'
import { useStreakActions } from 'src/store/streak'
import { useGetToast, useUIActions } from 'src/store/ui'
import type { Bet } from 'src/types/domain/Bet'
import type { BetDetails, BetType } from 'src/types/domain/BetDetails'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Marble } from 'src/types/domain/Marble'
import { isAuthenticatedPlayer, type AuthenticatedPlayer } from 'src/types/domain/Player'
import type { Race } from 'src/types/domain/Race'
import { BetMap } from 'src/types/mappers/BetMap'
import { isBalanceInsufficient } from 'src/utils/betGuards'
import { generateBetId } from 'src/utils/betUtils'
import { getSelectionTypeOptions, isCrashGameType } from 'src/utils/gameTypeUtils'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { isValidBetCombo } from 'src/utils/isValidBetCombo'
import { getLatestEvent } from 'src/utils/raceUtils'
import { hardCloseClock } from 'src/utils/hardCloseClock'
import { AutoBetPanel } from './components/AutoBetPanel'
import BetPane from './components/BetPane'
import ChooseMarblePane from './components/ChooseMarblePane'
import { NextRaceBetButton } from './components/NextRaceBetButton'
import Toast from './components/Toast'
import { useAutoBet } from './hooks/useAutoBet'
import { useCrashAutoBet } from './hooks/useCrashAutoBet'
import { NextRaceBetPanel } from './NextRaceBetPanel'

type Props = Readonly<{
  game: MarbleGame
  player: AuthenticatedPlayer
}>

export function PlaceBetPanel({ game, player }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const [selectedMarbles, setSelectedMarbles] = useState<Marble[]>([])
  const [selectedInventoryItemIds, setSelectedInventoryItemIds] = useState<string[]>([])
  const [isBettingAllowed, setIsBettingAllowed] = useState<boolean>(false)
  const [isPlacingBet, setIsPlacingBet] = useState<boolean>(false)
  const [isMaxPayoutReached, setIsMaxPayoutReached] = useState<boolean>(false)
  const [betType, setBetType] = useState<BetType>('PickWinner')
  const [selectedPositions, setSelectedPositions] = useState<Record<string, number>>({})
  const { playEffect } = useSoundEffect()
  const [lastBetConfig, setLastBetConfig] = useState<{
    mode: 'pickWinner' | 'pickOrder'
    positions: Record<string, number>
    selectedMarbles: Marble[]
    isExactOrder: boolean
    betAmount: string
  }>({
    mode: 'pickWinner',
    positions: {},
    selectedMarbles: [],
    isExactOrder: false,
    betAmount: '0'
  })
  const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false)

  const [showRepeatButton, setShowRepeatButton] = useState(false)

  const nextRaceBet = useGetNextRaceBet()
  const isPlacingNextRaceBet = useGetIsPlacingNextRaceBet()
  const toast = useGetToast()
  const betActions = useBetActions()
  const playerActions = usePlayerActions()
  const nextRaceBetActions = useNextRaceBetActions()
  const uiActions = useUIActions()
  const betConfig = useGetGameBetConfig(player)
  const playerChannel = useGetPlayerChannel()
  const streakActions = useStreakActions()
  const calculatePayout = usePayoutCalculation({ playerChannel, player, game })
  const { currentBet } = useGetCurrentBet()
  const isUnacceptedBet = useIsUnacceptedBet(currentBet?.id)
  const userHasBet = currentBet !== undefined

  // Should not be empty but just in case
  const defaultBetMultiplier = betConfig?.defaultBetMultiplier ?? FALLBACK_DEFAULT_BET_MULTIPLIER

  const getMaxBetAmount = useCallback(
    (betType: BetType) => {
      return betConfig?.limits[betType]?.max
        ? fromInteger(betConfig.limits[betType].max, player.balance.currency)
        : 0
    },
    [betConfig, player.balance.currency]
  )

  const getMinBetAmount = useCallback(
    (betType: BetType) => {
      return betConfig?.limits[betType]?.min
        ? fromInteger(betConfig.limits[betType].min, player.balance.currency)
        : 0
    },
    [betConfig, player.balance.currency]
  )

  const [potentialWin, setPotentialWin] = useState<string>('0.00')
  const [multiplier, setMultiplier] = useState<number>(0)
  const [probability, setProbability] = useState<number>(0)
  // Streak info from payout calculation
  const [streakInfo, setStreakInfo] = useState<{
    streakBoostPercent: number
    streakType: 'win' | 'loss'
    streakCount: number
  } | null>(null)
  const [betAmount, setBetAmount] = useState<string>(() => {
    const calculatedDefault = getMinBetAmount(betType) * defaultBetMultiplier
    const maxAllowed = Math.min(getMaxBetAmount(betType), player.balance.amount)

    // Only round up amounts >= 1 to clean whole numbers
    // Preserve decimal precision for small amounts (crypto, small denominations)
    if (calculatedDefault >= 1) {
      const rounded = Math.ceil(calculatedDefault)
      // Ensure rounded amount doesn't exceed max bet or balance
      return Math.min(rounded, maxAllowed).toString()
    }

    return calculatedDefault.toString()
  })
  const [maxBetAmount, setMaxBetAmount] = useState<number>(getMaxBetAmount(betType))
  const [minBetAmount, setMinBetAmount] = useState<number>(getMinBetAmount(betType))
  const [correlationId, setCorrelationId] = useState<string>()

  // Get auto-booster for the current game
  const autoBooster = useGetAutoBooster(game.id)

  // Get active bonus for the current game
  const activeBonus = useGetActiveBonus(game.id)

  // Auto-apply the oldest usable claimed free bet (FIFO, like chip placement).
  // No manual remove — claimed free bets are always consumed before real balance.
  useAutoApplyClassicFreeBet({ gameId: game.id, minBetAmount, betAmount, setBetAmount })

  // Get claimed item IDs and items from store
  const allActivatedItemIds = useInventoryStore((state) => state.activatedItemIds)
  const inventoryItems = useInventoryStore((state) => state.items)

  // Filter activated items to only those applicable to the current game
  const activatedItemIds = useMemo(
    () =>
      allActivatedItemIds.filter((id) => {
        const item = inventoryItems.find((i) => i.id === id)
        return item ? isItemApplicableToGame(item, game.id) : false
      }),
    [allActivatedItemIds, inventoryItems, game.id]
  )

  useEffect(() => {
    const baseMax = getMaxBetAmount(betType)
    const maxPayoutLimit = betConfig?.maxPayout
    const potentialPayout = toInteger(potentialWin, player.balance.currency)
    const isMaxPayoutReached = maxPayoutLimit && potentialPayout > maxPayoutLimit

    let adjustedMaxBetAmount = baseMax

    if (isMaxPayoutReached && multiplier > 0) {
      const maxPayoutLimitDecimal = fromInteger(maxPayoutLimit, player.balance.currency)
      // Round down to whole number to ensure we stay under max payout limit
      adjustedMaxBetAmount = Math.floor(maxPayoutLimitDecimal / multiplier)
    }

    setMaxBetAmount(adjustedMaxBetAmount)
  }, [
    betType,
    getMaxBetAmount,
    multiplier,
    betConfig?.maxPayout,
    player.balance.currency,
    potentialWin
  ])

  // Initialize selectedInventoryItemIds with auto-booster and all claimed items
  // Crash games do not support boosters/inventory items
  useEffect(() => {
    if (isCrashGameType(game.track.gameType)) {
      setSelectedInventoryItemIds([])
      return
    }

    const inventoryItemIds: string[] = []

    // Add auto-booster (automatically applied)
    if (autoBooster) {
      inventoryItemIds.push(autoBooster.id)
    }

    // Add all manually claimed items
    inventoryItemIds.push(...activatedItemIds)

    setSelectedInventoryItemIds(inventoryItemIds)
  }, [autoBooster, autoBooster?.id, activatedItemIds, game.track.gameType])

  useEffect(() => {
    setMaxBetAmount(getMaxBetAmount(betType))
  }, [betType]) // getMaxBetAmount not needed as it uses use-callback

  useEffect(() => {
    setMinBetAmount(getMinBetAmount(betType))
  }, [betType])

  const currentRace = game.races[0]
  const latestEvent = getLatestEvent(currentRace.raceEvents)

  // The hard close (rev 4, DESIGN §6, decision D8): on a round whose race_ready
  // says bet_close "hard", clicks are refused from the board's lock, the first
  // of race_starting's arrival and its own countdown; the "place bet on next
  // game" panel stays. Nothing is sent at the lock (every click sent at once).
  // Every other round: `hard` false, and the panel is today's.
  const betLock = useBetLock({
    race: currentRace,
    previousRace: game.races[1],
    onLock: () => undefined
  })
  const hardLocked = betLock.hard && betLock.lockedBy !== undefined
  /**
   * The lock as it stands at THIS moment, not at the last render: a click is
   * judged when it lands (`hardCloseClock` reads the same two signals).
   */
  const lockedNow = useCallback(() => {
    const clock = hardCloseClock(game.races[0], game.races[1])
    return clock !== null && clock.lockedBy !== undefined
  }, [game.races])

  // Determine if we're betting on the current race (for lightning round boost calculation)
  // Only pass roundId during race_ready/race_starting when betting on current race
  const isBettingOnCurrentRace =
    latestEvent?.eventType === 'race_ready' || latestEvent?.eventType === 'race_starting'

  useEffect(() => {
    if (!isValidBetCombo(betType, selectedMarbles.length)) return

    let isCancelled = false
    let retryTimeout: NodeJS.Timeout | null = null

    const updatePayout = async () => {
      if (isCancelled) return

      setIsMaxPayoutReached(false)
      const payoutResult = await calculatePayout({
        betAmount,
        selectedMarbles,
        betType,
        inventoryItemIds: selectedInventoryItemIds,
        // Pass roundId when betting on current race to enable lightning round boost calculation
        // After race_started, we're betting on next race and shouldn't include lightning boost
        roundId: isBettingOnCurrentRace ? currentRace.uuid : undefined
      })

      if (isCancelled) return

      if (payoutResult) {
        const potentialWin = payoutResult.payout ?? 0
        const maxPayoutLimit = betConfig?.maxPayout

        setPotentialWin(potentialWin.toString())

        // Do some front-end validation based on the maxPayout limit
        if (maxPayoutLimit && potentialWin > fromInteger(maxPayoutLimit, player.balance.currency)) {
          setIsMaxPayoutReached(true)
          uiActions.betRejected('MaxPayoutReached')
        }

        // Additional validation for boosters/multipliers that could cause max payout to be exceeded
        if (maxPayoutLimit && payoutResult.multiplier > 0) {
          const maxPayoutLimitDecimal = fromInteger(maxPayoutLimit, player.balance.currency)
          const maxAllowedBetAmount = maxPayoutLimitDecimal / payoutResult.multiplier

          if (parseFloat(betAmount) > maxAllowedBetAmount) {
            setIsMaxPayoutReached(true)
            uiActions.betRejected('MaxPayoutReached')
          }
        }

        if (payoutResult.multiplier) {
          setMultiplier(payoutResult.multiplier)
        }

        if (payoutResult.probability) {
          setProbability(payoutResult.probability)
        }

        // Update streak from backend - this is the source of truth
        if (
          payoutResult.streakBoostPercent &&
          payoutResult.streakType &&
          payoutResult.streakCount
        ) {
          const streakData = {
            streakBoostPercent: payoutResult.streakBoostPercent,
            streakType: payoutResult.streakType,
            streakCount: payoutResult.streakCount
          }
          setStreakInfo(streakData)
          streakActions.setStreak(streakData)
        } else {
          setStreakInfo(null)
          streakActions.clearStreak()
        }
      } else if (!isCancelled) {
        // Calculation failed - retry after a short delay
        retryTimeout = setTimeout(() => void updatePayout(), 300)
      }
    }

    void updatePayout()

    return () => {
      isCancelled = true
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }
    }
  }, [
    betAmount,
    selectedMarbles,
    betType,
    selectedInventoryItemIds,
    calculatePayout,
    betConfig?.maxPayout,
    uiActions,
    streakActions,
    player.balance.currency,
    isBettingOnCurrentRace,
    currentRace.uuid
  ])

  const currentCurrency = useMemo(
    () => getCurrencySymbol(isAuthenticatedPlayer(player) ? player.balance.currency : 'USD'),
    [player]
  )

  // Update selection tracking
  useEffect(() => {
    if (
      selectedMarbles.length > 0 &&
      JSON.stringify(selectedMarbles) !== JSON.stringify(lastBetConfig.selectedMarbles)
    ) {
      setShowRepeatButton(false)
    }
  }, [selectedMarbles, lastBetConfig])

  // Revert current bet if it is not accepted after a certain time
  useEffect(() => {
    // eslint-disable-next-line prefer-const -- Timer variable needs to be mutable for clearing timeout
    let timer: NodeJS.Timeout | undefined = setTimeout(() => {
      if (currentBet && isUnacceptedBet) {
        betActions.betUnaccepted(currentBet)
        uiActions.betUnaccepted(currentBet)
        playerActions.betUnaccepted(currentBet)

        datadogLogs.logger.info('bet_no_backend_response', {
          user_event: {
            bet: currentBet,
            correlationId
          }
        })
      }
    }, BET_CONFIG.WAIT_FOR_ACCEPTANCE_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [currentBet, isUnacceptedBet, betActions, uiActions, playerActions, correlationId])

  // Auto-select first marble for crash game type (betting options are hidden)
  useEffect(() => {
    if (game.track.gameType === 'crash' && selectedMarbles.length === 0) {
      const firstActiveMarble = game.marbles.find((m) => m.active)
      if (firstActiveMarble) {
        setSelectedMarbles([firstActiveMarble])
      }
    }
  }, [game.track.gameType, game.marbles, selectedMarbles.length])

  useEffect(() => {
    const bettingAllowed =
      (latestEvent?.eventType === 'race_ready' || latestEvent?.eventType === 'race_starting') &&
      !hardLocked
    setIsBettingAllowed(bettingAllowed)

    // Re-open the "Place bet on next game" panel when betting is no longer allowed
    // This ensures the panel is always open by default for the next race
    // Only if user hasn't placed a bet and doesn't have a pending next race bet
    // For crash, don't auto-open — let the NextRaceBetButton show instead
    if (!bettingAllowed && !userHasBet && !nextRaceBet && game.track.gameType !== 'crash') {
      nextRaceBetActions.setIsPlacingNextRaceBet(true)
    }
  }, [latestEvent, userHasBet, nextRaceBet, nextRaceBetActions, hardLocked])

  const [mode, setMode] = useState<'pickWinner' | 'pickOrder'>('pickWinner')
  // Not while the balance is still loading: the placeholder is 0, and the effect below would
  // tell the host page the player is out of money on every launch.
  const insufficientBalance: boolean = isBalanceInsufficient({
    balanceLoaded: player.balanceLoaded,
    balanceAmount: player.balance.amount,
    stake: parseFloat(betAmount),
    hasActiveBonus: Boolean(activeBonus)
  })

  useEffect(() => {
    if (insufficientBalance) {
      emitBalanceInsufficient({
        currentBalance: player.balance.amount,
        currency: player.balance.currency
      })
    }
  }, [insufficientBalance, player.balance.amount, player.balance.currency])

  useEffect(() => {
    if (
      latestEvent?.eventType === 'race_ready' &&
      !userHasBet &&
      lastBetConfig.selectedMarbles.length > 0
    ) {
      setShowRepeatButton(true)
    }
  }, [latestEvent, userHasBet, lastBetConfig.selectedMarbles.length])

  const canPlaceBet: boolean = useMemo(() => {
    if (isPlacingBet || (!isBettingAllowed && !isPlacingNextRaceBet)) {
      return false
    }

    switch (mode) {
      case 'pickWinner':
        return selectedMarbles.length > 0
      case 'pickOrder':
        if (betType === 'ReverseForecast' || betType === 'CombinationTricast') {
          // For "any order" bets, we just need the correct number of marbles selected
          return (
            (betType === 'ReverseForecast' && selectedMarbles.length === 2) ||
            (betType === 'CombinationTricast' && selectedMarbles.length === 3)
          )
        } else if (betType === 'StraightForecast' || betType === 'Tricast') {
          // For "exact order" bets, we need positions set for all selected marbles
          return (
            ((betType === 'StraightForecast' && selectedMarbles.length === 2) ||
              (betType === 'Tricast' && selectedMarbles.length === 3)) &&
            selectedMarbles.every((marble) => {
              const position = selectedPositions[marble.uuid] ?? 0
              return position > 0
            })
          )
        }
    }

    return false
  }, [
    isPlacingBet,
    isBettingAllowed,
    isPlacingNextRaceBet,
    mode,
    betType,
    selectedMarbles,
    selectedPositions
  ])

  useEffect(() => {
    // While a free bet is active its amount is authoritative — the stake is locked to it by
    // useAutoApplyClassicFreeBet. Skip the min/max clamp so it can't fight the lock (and, if a
    // free bet ever exceeds maxBet, can't ping-pong the stake between the two).
    if (activeBonus) return

    let newBetAmount

    if (parseFloat(betAmount) > maxBetAmount) {
      newBetAmount = maxBetAmount
    } else if (parseFloat(betAmount) > minBetAmount) {
      newBetAmount = betAmount
    } else {
      newBetAmount = minBetAmount
    }

    setBetAmount(newBetAmount.toString())
  }, [maxBetAmount, betAmount, minBetAmount, activeBonus])

  const handlePositionSelection = useCallback(
    (marble: Marble, position: number) => {
      setSelectedPositions((prevPositions) => {
        const newPositions = { ...prevPositions }
        const conflictingMarble = Object.keys(newPositions).find(
          (key) => newPositions[key] === position
        )

        if (conflictingMarble) {
          newPositions[conflictingMarble] = 0
        }

        newPositions[marble.uuid] = position

        if (betType === 'StraightForecast') {
          const otherMarble = selectedMarbles.find((m) => m.uuid !== marble.uuid)
          if (otherMarble) {
            newPositions[otherMarble.uuid] = position === 1 ? 2 : 1
          }
        }

        return newPositions
      })
    },
    [betType, selectedMarbles]
  )

  const toggleMarbleSelection = useCallback(
    (marble: Marble) => {
      const isSelected = selectedMarbles.some((m) => m.uuid === marble.uuid)

      if (isSelected) {
        playEffect('marbleSelectedReverse')
      } else {
        playEffect('marbleSelected')
      }

      setSelectedMarbles((prev) =>
        isSelected ? prev.filter((m) => m.uuid !== marble.uuid) : [...prev, marble]
      )

      setSelectedPositions({})
    },
    [selectedMarbles, playEffect]
  )

  const submitBet = useCallback(
    async (
      betAmount: number,
      currentRace: Race,
      selectedMarbles: Marble[],
      autoCashoutMultiplier?: number,
      /**
       * A hard round's click goes out as it lands: no quote is awaited before
       * the push (the panel's own quote, or 0, stands in for the optimistic
       * bet's potential payout until bet:accepted brings Go's).
       */
      knownPayout?: number
    ) => {
      setIsPlacingBet(true)
      try {
        if (!playerChannel) {
          console.error('Error placing bet: no player channel')
          return
        }

        const rewardResult =
          knownPayout !== undefined
            ? { payout: knownPayout }
            : await calculatePayout({
                betAmount: betAmount.toString(),
                selectedMarbles,
                betType,
                inventoryItemIds: selectedInventoryItemIds
              })

        const betWithoutId: Omit<Bet, 'id'> = {
          playerId: player.id,
          raceId: currentRace.uuid,
          raceConfigurationId: currentRace.raceConfigurationId,
          betAmount,
          currency: player.balance.currency,
          betDetails: {
            type: betType,
            selectedItems: selectedMarbles.map((m) => m.uuid),
            ...(selectedInventoryItemIds.length
              ? { inventoryItemIds: selectedInventoryItemIds }
              : {})
          } as BetDetails,
          status: 'pending',
          potentialPayout: rewardResult?.payout || 0,
          createdAt: new Date().toISOString(),
          // Funded by a free bet (activeBonus) — stake not debited, so a
          // purge/timeout of this optimistic bet must not refund it.
          isFreeBet: Boolean(activeBonus),
          // Crash game auto-cashout multiplier (if enabled)
          ...(autoCashoutMultiplier !== undefined && { autoCashoutMultiplier })
        }

        const bet: Bet = {
          id: Effect.runSync(generateBetId(betWithoutId)),
          ...betWithoutId
        }

        const correlationId = uuidv7()
        setCorrelationId(correlationId)
        try {
          const payload = BetMap.toPlaceBetRequestedDTO(player.sessionId, bet, correlationId)

          datadogLogs.logger.info('place_bet', {
            user_event: {
              payload,
              correlationId
            }
          })

          playerChannel.push('client:bet:initiated', payload)
        } catch (error) {
          console.error('Placing bet failed:', error)
          return
        }

        betActions.betPlaced(bet)
        playerActions.betPlaced(bet, Boolean(activeBonus))
        playEffect('betPlaced')
      } catch (error) {
        console.error('Placing bet failed:', error)
      } finally {
        setIsPlacingBet(false)
      }
    },
    [
      playerChannel,
      betType,
      player,
      game,
      betActions,
      playerActions,
      playEffect,
      correlationId,
      selectedInventoryItemIds
    ]
  )

  // Add click sound to buttons
  const handleButtonClick = (callback: () => void) => {
    playEffect('click')
    callback()
  }

  const handleResetSelection = () => {
    playEffect('cancel')
    setSelectedMarbles([])
    setSelectedPositions({})
    // Only show repeat button if we have a previous bet configuration
    setShowRepeatButton(lastBetConfig.selectedMarbles.length > 0)
  }

  const handlePlaceBet = async (betValue: string, autoCashoutMultiplier?: number) => {
    if (currentBet) {
      console.warn('A bet has already been placed for this race')
      return
    }

    setLastBetConfig({
      mode,
      positions: selectedPositions,
      selectedMarbles,
      isExactOrder: betType === 'StraightForecast' || betType === 'Tricast',
      betAmount
    })

    const betAmountNumber = parseFloat(betValue)

    if (betAmountNumber < minBetAmount) {
      uiActions.betValueOutOfRange('belowRange')
      return
    }

    if (betAmountNumber > maxBetAmount) {
      uiActions.betValueOutOfRange('aboveRange')
      return
    }

    // Safety: never place a bet before the real balance has loaded. The
    // non-blocking launch renders the game before the operator-wallet balance
    // arrives; a bonus bet would otherwise skip the cash check below.
    if (!player.balanceLoaded) return

    if (!activeBonus && betAmountNumber > player.balance.amount) {
      uiActions.storeBalanceCheckFailed()
      return
    }

    if (selectedMarbles.length > 0 && betAmountNumber > 0) {
      const currentRace = game.races[0]
      // A hard round's click is judged as it lands: from the lock on, it is
      // refused for this round (a next-game bet is queued instead, below).
      const hard = betLock.hard
      const refusedAtLock = hard && lockedNow()
      const quote = hard ? parseFloat(potentialWin) || 0 : undefined

      if (!isPlacingNextRaceBet) {
        if (refusedAtLock) {
          logClickRefusedAtLock({
            board: 'click',
            then: 'next_game_panel',
            race: currentRace,
            previousRace: game.races[1]
          })
          playEffect('cancel')
          nextRaceBetActions.setIsPlacingNextRaceBet(true)
          return
        }
        // If player is placing bet on current
        void submitBet(betAmountNumber, currentRace, selectedMarbles, autoCashoutMultiplier, quote)
        return
      }

      // If player is placing bet on next race but the race is already in race_ready/race_starting state
      const allowedEvents = ['race_ready', 'race_starting']
      const roundOpen = Boolean(latestEvent && allowedEvents.includes(latestEvent.eventType))
      if (roundOpen && !refusedAtLock) {
        await submitBet(betAmountNumber, currentRace, selectedMarbles, autoCashoutMultiplier, quote)
        nextRaceBetActions.clearNextRaceBet()
      } else {
        // The round is still open but its board has locked: the click is
        // refused for this round, and the bet goes to the next game.
        if (roundOpen && refusedAtLock) {
          logClickRefusedAtLock({
            board: 'click',
            then: 'queued_next_game',
            race: currentRace,
            previousRace: game.races[1]
          })
        }
        nextRaceBetActions.setNextRaceBet({
          betAmount: betValue,
          currency: player.balance.currency,
          selectedMarbles,
          betType,
          selectedPositions,
          gameId: game.id,
          selectedInventoryItemIds
        })

        playEffect('betPlaced')
        nextRaceBetActions.setIsPlacingNextRaceBet(false)
      }
    }
  }

  useEffect(() => {
    if (!latestEvent) return

    if (nextRaceBet && latestEvent.eventType === 'race_ready') {
      // Short delay before placing bet
      const timer = setTimeout(() => {
        // A hard round that has locked by now keeps the bet queued for the next game.
        if (betLock.hard && lockedNow()) return
        void submitBet(
          parseFloat(nextRaceBet.betAmount),
          currentRace,
          nextRaceBet.selectedMarbles,
          undefined,
          betLock.hard ? 0 : undefined
        )
        nextRaceBetActions.clearNextRaceBet()
      }, 2000) // 2 second delay

      return () => {
        clearTimeout(timer)
      }
    }
  }, [
    nextRaceBet,
    latestEvent?.eventType,
    latestEvent,
    submitBet,
    currentRace,
    nextRaceBetActions,
    betLock.hard,
    lockedNow
  ])

  const handleNextRaceBetAction = (openBetPanel: boolean) => {
    // Stop auto betting if we click edit
    if (autoBet.isActive) {
      autoBet.stopBetting({
        scope: 'nextBetPanel',
        reason: 'editButtonClicked'
      })
    }

    // Set all the selections if we have a next race bet and we're opening the panel by clicking edit
    if (nextRaceBet && openBetPanel) {
      setMode(nextRaceBet.betType === 'PickWinner' ? 'pickWinner' : 'pickOrder')
      setBetAmount(nextRaceBet.betAmount)
      setSelectedMarbles(nextRaceBet.selectedMarbles)
      setBetType(nextRaceBet.betType)
      setSelectedPositions(nextRaceBet.selectedPositions)
      setSelectedInventoryItemIds(nextRaceBet.selectedInventoryItemIds || [])
    } else {
      // Reset all state if we're canceling
      setMode('pickWinner')
      setBetAmount((getMinBetAmount('PickWinner') * defaultBetMultiplier).toString())
      setSelectedMarbles([])
      setBetType('PickWinner')
      setSelectedPositions({})
      if (autoBet.isActive) {
        autoBet.stopBetting({
          scope: 'nextBetPanel',
          reason: 'cancelButtonClicked'
        })
      }
    }

    if (openBetPanel) {
      nextRaceBetActions.setIsPlacingNextRaceBet(true)
    }
    nextRaceBetActions.clearNextRaceBet()
  }

  // Check if this is a crash game type
  const isCrashGame = isCrashGameType(game.track.gameType)

  // Standard auto-bet hook (for non-crash games)
  const standardAutoBet = useAutoBet({
    onBet: handlePlaceBet,
    betAmount,
    insufficientBalance,
    hasExistingBet: Boolean(currentBet),
    isRaceReady: latestEvent?.eventType === 'race_ready',
    onReset: handleResetSelection,
    playerId: player.id
  })

  // Crash auto-bet hook (for crash games - includes auto-cashout)
  const crashAutoBet = useCrashAutoBet({
    onBet: handlePlaceBet,
    betAmount,
    insufficientBalance,
    hasExistingBet: Boolean(currentBet),
    isRaceReady: latestEvent?.eventType === 'race_ready',
    playerId: player.id
  })

  // Use the appropriate auto-bet hook based on game type
  const autoBet = isCrashGame ? crashAutoBet : standardAutoBet

  useEffect(() => {
    if (autoBet.isActive) {
      autoBet.stopBetting({
        scope: 'betPanel',
        reason: 'inputChanged'
      })
    }
  }, [selectedMarbles, betType, betAmount])

  useEffect(() => {
    if (!userHasBet && lastBetConfig.selectedMarbles.length > 0) {
      setShowRepeatButton(true)
    }
  }, [userHasBet, lastBetConfig])

  const handleRepeatLastBet = () => {
    setMode(lastBetConfig.mode)
    setSelectedPositions(lastBetConfig.positions)
    setSelectedMarbles(lastBetConfig.selectedMarbles)
    setBetAmount(lastBetConfig.betAmount)
    setShowRepeatButton(false)
  }

  const getBettingDisabledInfo = useCallback(() => {
    const { singular, plural } = getSelectionTypeOptions(game.track.gameType, t)

    if (insufficientBalance) {
      return t('Insufficient Balance')
    }

    if (isMaxPayoutReached) {
      return t('Max Payout Reached')
    }

    if (selectedMarbles.length === 0) {
      return t('Select a {{type}} First', { type: singular })
    }

    if (mode === 'pickOrder' && selectedMarbles.length < 2) {
      return t('Select at least 2 {{type}}', { type: plural })
    }

    const isExactOrder = betType === 'StraightForecast' || betType === 'Tricast'
    if (
      mode === 'pickOrder' &&
      isExactOrder &&
      !selectedMarbles.every((marble) => selectedPositions[marble.uuid])
    ) {
      return t('Select {{type}} Positions', { type: singular })
    }

    return ''
  }, [
    isMaxPayoutReached,
    insufficientBalance,
    selectedMarbles.length,
    mode,
    betType,
    selectedPositions,
    game.track.gameType,
    t
  ])

  if (!betConfig) {
    return null
  }

  const handleStopAutoBetting = () => {
    autoBet.stopBetting({
      scope: 'autoBetPanel',
      reason: 'cancelButtonClicked'
    })

    setMode('pickWinner')
    setSelectedMarbles([])
    setSelectedPositions({})
  }

  return (
    <div className="marbles-container--place-bet">
      {toast.show ? (
        <Toast
          message={t(toast.message)}
          onClose={uiActions.closeButtonClicked}
          type={toast.type}
        />
      ) : null}

      {currentBet && autoBet.isActive ? (
        <AutoBetPanel
          onStopBetting={handleStopAutoBetting}
          autoCashoutEnabled={
            isCrashGame && 'autoCashoutEnabled' in autoBet
              ? (autoBet.autoCashoutEnabled as boolean)
              : undefined
          }
          autoCashoutMultiplier={
            isCrashGame && 'autoCashoutMultiplier' in autoBet
              ? (autoBet.autoCashoutMultiplier as number)
              : undefined
          }
        />
      ) : null}

      {!isBettingAllowed && !isPlacingNextRaceBet && !nextRaceBet && !userHasBet ? (
        <NextRaceBetButton
          game={game}
          onShowPlaceBet={() => {
            nextRaceBetActions.setIsPlacingNextRaceBet(true)
          }}
        />
      ) : null}

      {nextRaceBet ? (
        <NextRaceBetPanel
          game={game}
          isRaceReady={
            latestEvent?.eventType === 'race_ready' || latestEvent?.eventType === 'race_starting'
          }
          nextRaceBet={nextRaceBet}
          onCancel={() => {
            handleNextRaceBetAction(false)
          }}
          onEdit={() => {
            handleNextRaceBetAction(true)
          }}
          setPlacingNextRaceBet={nextRaceBetActions.setIsPlacingNextRaceBet}
        />
      ) : null}

      {((!isBettingAllowed && isPlacingNextRaceBet) || (isBettingAllowed && !nextRaceBet)) &&
      !userHasBet ? (
        <div className="marbles-container marbles-race__current">
          <div className="marbles-container__inner">
            <div className="place-bet">
              {game.track.gameType !== 'crash' && (
                <ChooseMarblePane
                  autoBet={{
                    isEnabled: autoBet.isEnabled,
                    isActive: autoBet.isActive,
                    toggle: autoBet.toggle
                  }}
                  betType={betType}
                  currentRace={currentRace}
                  game={game}
                  handleButtonClick={handleButtonClick}
                  handlePositionSelection={handlePositionSelection}
                  handleRepeatLastBet={handleRepeatLastBet}
                  handleResetSelection={handleResetSelection}
                  initialIsExactOrder={lastBetConfig.isExactOrder}
                  isRaceReady={
                    latestEvent?.eventType === 'race_ready' ||
                    latestEvent?.eventType === 'race_starting'
                  }
                  mode={mode}
                  onAdvancedOptionsChange={setShowAdvancedOptions}
                  selectedMarbles={selectedMarbles}
                  selectedPositions={selectedPositions}
                  setBetType={setBetType}
                  setMode={setMode}
                  setSelectedMarbles={setSelectedMarbles}
                  showAdvancedOptions={showAdvancedOptions}
                  showRepeatButton={showRepeatButton}
                  toggleMarbleSelection={toggleMarbleSelection}
                />
              )}
              <BetPane
                autoBet={{
                  isEnabled: autoBet.isEnabled,
                  isActive: autoBet.isActive,
                  toggle: autoBet.toggle,
                  startBetting: autoBet.startBetting,
                  stopBetting: () => {
                    autoBet.stopBetting({})
                  },
                  // Crash-specific properties (only available for crash games)
                  autoCashoutEnabled:
                    isCrashGame && 'autoCashoutEnabled' in autoBet
                      ? (autoBet.autoCashoutEnabled as boolean)
                      : undefined,
                  autoCashoutMultiplier:
                    isCrashGame && 'autoCashoutMultiplier' in autoBet
                      ? (autoBet.autoCashoutMultiplier as number)
                      : undefined,
                  toggleAutoCashout:
                    isCrashGame && 'toggleAutoCashout' in autoBet
                      ? (autoBet.toggleAutoCashout as () => void)
                      : undefined,
                  setAutoCashoutMultiplier:
                    isCrashGame && 'setAutoCashoutMultiplier' in autoBet
                      ? (autoBet.setAutoCashoutMultiplier as (multiplier: number) => void)
                      : undefined
                }}
                balance={player.balance}
                betAmount={betAmount}
                betType={betType}
                bettingDisabled={
                  !canPlaceBet || insufficientBalance || isMaxPayoutReached || !player.balanceLoaded
                }
                bettingDisabledInfo={getBettingDisabledInfo().toString()}
                chipMultipliers={betConfig.chipMultipliers}
                currentCurrency={currentCurrency}
                game={game}
                isPlacingBet={isPlacingBet}
                maxBetAmount={parseFloat(maxBetAmount.toString())}
                minBetAmount={parseFloat(minBetAmount.toString())}
                multiplier={multiplier}
                placeBet={handlePlaceBet}
                potentialWin={potentialWin}
                probability={probability}
                selectedMarblesCount={selectedMarbles.length}
                setBetAmount={setBetAmount}
                setMode={setMode}
                setSelectedMarbles={setSelectedMarbles}
                setSelectedPositions={setSelectedPositions}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
